/**
 * Light-weight allow-list HTML sanitizer for AI-streamed output before it
 * lands in `dangerouslySetInnerHTML`. The set of allowed tags matches what
 * the system prompt instructs the model to emit; everything else is either
 * dropped (dangerous tags) or unwrapped (unknown but harmless tags).
 *
 * Why not DOMPurify? — no project dep yet; AI output is already constrained
 * by the system prompt. If the threat surface ever grows, swap this for
 * DOMPurify with no callsite changes.
 */

const ALLOWED_TAGS = new Set([
    'P',
    'STRONG',
    'EM',
    'B',
    'I',
    'U',
    'UL',
    'OL',
    'LI',
    'BR',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'BLOCKQUOTE',
]);

/** Tags whose CONTENT must also be discarded (script execution, etc). */
const DROP_WITH_CONTENT = new Set([
    'SCRIPT',
    'STYLE',
    'IFRAME',
    'OBJECT',
    'EMBED',
    'LINK',
    'META',
    'FORM',
    'INPUT',
    'BUTTON',
]);

/**
 * gpt-4o-mini occasionally leaks markdown markers (`**bold**`, `__bold__`)
 * into the HTML output despite the system prompt forbidding it. Without this
 * step, those literal asterisks/underscores render verbatim inside the
 * rich-text editor. Run BEFORE DOMParser — pure string substitution on text.
 * Single `*` / `_` are intentionally NOT converted (false-positive risk on
 * arithmetic / underscored identifiers in narrative text).
 */
const convertMarkdownLeaks = (s) =>
    s
        .replace(/\*\*(\S(?:.*?\S)?)\*\*/g, '<strong>$1</strong>')
        .replace(/__(\S(?:.*?\S)?)__/g, '<strong>$1</strong>');

/**
 * @param {string} html  raw HTML string (possibly mid-stream / unbalanced)
 * @returns {string}     sanitized HTML safe for dangerouslySetInnerHTML
 */
export const sanitizeAiHtml = (html) => {
    if (!html) return '';
    if (typeof window === 'undefined' || !window.DOMParser) return ''; // SSR guard

    const normalized = convertMarkdownLeaks(html);

    // Wrap in a container so `body.firstChild` is our root and we don't lose
    // top-level siblings. The browser's parser auto-closes unbalanced tags.
    const doc = new DOMParser().parseFromString(`<div>${normalized}</div>`, 'text/html');
    const root = doc.body.firstChild;
    if (!root) return '';

    walk(root);
    return root.innerHTML;
};

function walk(node) {
    // Iterate over a snapshot — we mutate childNodes during the walk.
    const children = Array.from(node.childNodes);
    for (const child of children) {
        if (child.nodeType !== 1 /* ELEMENT_NODE */) continue;

        const tag = child.tagName;

        if (DROP_WITH_CONTENT.has(tag)) {
            child.parentNode.removeChild(child);
            continue;
        }

        if (!ALLOWED_TAGS.has(tag)) {
            // Unwrap: lift the children out, then remove the wrapper.
            while (child.firstChild) {
                child.parentNode.insertBefore(child.firstChild, child);
            }
            child.parentNode.removeChild(child);
            continue;
        }

        // Strip ALL attributes (defends against `style`, `on*`, etc.)
        for (const attr of Array.from(child.attributes)) {
            child.removeAttribute(attr.name);
        }
        walk(child);
    }
}
